/**
 * Money is always an integer number of paise. Never use floating-point rupees in calculations.
 * rupeesToPaise parses the decimal string directly, so "0.1" + "0.2" style float errors cannot happen.
 */
export type Paise = number;

export function rupeesToPaise(value: string | number): Paise {
  const s = String(value).trim().replace(/,/g, "");
  const m = /^(-)?(\d+)(?:\.(\d{1,2}))?$/.exec(s);
  if (!m) throw new Error(`Invalid amount: ${value}`);
  const paise = Number(m[2]) * 100 + Number((m[3] ?? "").padEnd(2, "0"));
  if (!Number.isSafeInteger(paise)) throw new Error(`Amount too large: ${value}`);
  return m[1] ? -paise : paise;
}

/** 12345 -> "123.45" (no grouping; for inputs and CSV). */
export function paiseToRupees(paise: Paise): string {
  assertPaise(paise);
  const sign = paise < 0 ? "-" : "";
  const abs = Math.abs(paise);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
}

/** 1234567 -> "₹12,345.67" with Indian digit grouping (lakh / crore). */
export function formatINR(paise: Paise, opts: { symbol?: boolean; decimals?: boolean } = {}): string {
  assertPaise(paise);
  const { symbol = true, decimals = true } = opts;
  const sign = paise < 0 ? "-" : "";
  const abs = Math.abs(paise);
  const rupees = String(Math.floor(abs / 100));
  const last3 = rupees.slice(-3);
  const rest = rupees.slice(0, -3);
  const grouped = rest ? `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",")},${last3}` : last3;
  const frac = decimals ? `.${String(abs % 100).padStart(2, "0")}` : "";
  return `${sign}${symbol ? "₹" : ""}${grouped}${frac}`;
}

/** Split a total into n parts that add up exactly (the first parts absorb the remainder). */
export function allocate(total: Paise, parts: number): Paise[] {
  assertPaise(total);
  if (!Number.isInteger(parts) || parts <= 0) throw new Error("parts must be a positive integer");
  const base = Math.trunc(total / parts);
  const rem = total - base * parts;
  return Array.from({ length: parts }, (_, i) => base + (i < Math.abs(rem) ? Math.sign(rem) : 0));
}

/** Percentage of an amount in basis points (1% = 100 bps), rounded half-up to the paisa. */
export function percentOf(amount: Paise, basisPoints: number): Paise {
  assertPaise(amount);
  return Math.round((amount * basisPoints) / 10_000);
}

export function assertPaise(value: unknown): asserts value is Paise {
  if (typeof value !== "number" || !Number.isSafeInteger(value)) throw new Error(`Not an integer paise amount: ${String(value)}`);
}

/** Indian currency denominations used for cash counting at shift close (paise per unit). */
export const DENOMINATIONS: readonly { label: string; paise: Paise }[] = [
  { label: "₹2000", paise: 200000 }, { label: "₹500", paise: 50000 }, { label: "₹200", paise: 20000 },
  { label: "₹100", paise: 10000 }, { label: "₹50", paise: 5000 }, { label: "₹20", paise: 2000 },
  { label: "₹10", paise: 1000 }, { label: "₹5", paise: 500 }, { label: "₹2", paise: 200 }, { label: "₹1", paise: 100 },
];

/** Total of a denomination count map {"50000": 3, "10000": 2} -> paise. */
export function countDenominations(counts: Record<string, number>): Paise {
  let total = 0;
  for (const [unit, n] of Object.entries(counts)) {
    const u = Number(unit);
    if (!DENOMINATIONS.some((d) => d.paise === u)) throw new Error(`Unknown denomination ${unit}`);
    if (!Number.isInteger(n) || n < 0) throw new Error(`Invalid count for ${unit}`);
    total += u * n;
  }
  return total;
}
